import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import {
  BookOpen,
  Target,
  Play,
  Filter,
  Search,
  Clock,
  Star,
  ChevronRight,
  Grid,
  List,
  Microscope
} from 'lucide-react'
import Navigation from '../../components/common/Navigation'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import Footer from '../../components/common/Footer'
import VideoSimulationService, { VideoSimulation as IVideoSimulation } from '../../services/video/VideoSimulationService'
import VideoSimulation from '../../components/interactive/shared/simulations/VideoSimulation'

interface ContentItem {
  id: string
  type: 'bai-hoc' | 'trac-nghiem' | 'mo-phong'
  title: string
  description: string
  lop: number
  chuong: number
  bai?: number
  difficulty: 'dễ' | 'trung bình' | 'khó'
  duration: number
  rating: number
  tags: string[]
  thumbnail?: string
  url: string
  videoPath?: string
  chuongName?: string
  baiName?: string
}

const TongHop: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [contentItems, setContentItems] = useState<ContentItem[]>([])
  const [videoSimulations, setVideoSimulations] = useState<ContentItem[]>([])
  const [filteredItems, setFilteredItems] = useState<ContentItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState<string>('mo-phong') // Mặc định chỉ hiển thị mô phỏng
  const [selectedClass, setSelectedClass] = useState<string>(searchParams.get('class') || 'all')
  const [selectedBai, setSelectedBai] = useState<string>(searchParams.get('bai') || 'all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  // Không cần selectedVideo state nữa vì video hiển thị inline

  // Lấy danh sách bài học theo lớp
  const getBaiListByClass = (lop: string): number[] => {
    switch (lop) {
      case '10':
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]
      case '11':
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18]
      case '12':
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22]
      default:
        return []
    }
  }

  // Mock data - chỉ bài học (không có trắc nghiệm)
  const mockData: ContentItem[] = [
    // Bài học
    {
      id: 'bai-1',
      type: 'bai-hoc',
      title: 'Mở đầu về Vật lí',
      description: 'Giới thiệu về môn Vật lí và phương pháp học tập hiệu quả',
      lop: 10,
      chuong: 0,
      bai: 1,
      difficulty: 'dễ',
      duration: 45,
      rating: 4.8,
      tags: ['cơ bản', 'giới thiệu'],
      url: '/lop/10/chuong/0/bai/bai-1'
    },
    {
      id: 'bai-2',
      type: 'bai-hoc',
      title: 'Chuyển động thẳng đều',
      description: 'Nghiên cứu chuyển động thẳng đều và các đặc điểm',
      lop: 10,
      chuong: 1,
      bai: 1,
      difficulty: 'trung bình',
      duration: 60,
      rating: 4.6,
      tags: ['động học', 'chuyển động'],
      url: '/lop/10/chuong/1/bai/bai-1'
    },
  ]

  useEffect(() => {
    // Load video simulations from VideoSimulationService
    const loadVideoSimulations = async () => {
      try {
        console.log('🔄 Loading video simulations from VideoSimulationService...')
        const loadedSimulations = await VideoSimulationService.getVideoSimulations()
        console.log('📊 Loaded video simulations:', loadedSimulations.length, 'items')
        console.log('🎬 Video simulations:', loadedSimulations)

        // Store video simulations
        setVideoSimulations(loadedSimulations)

        // Set loading false sau khi load xong
        setLoading(false)

        console.log('✅ videoSimulations state set successfully')
      } catch (error) {
        console.error('❌ Error loading video simulations:', error)
        setVideoSimulations([])
        setLoading(false) // Set loading false ngay cả khi có lỗi
      }
    }

    loadVideoSimulations()

    // OLD CODE - Keep for reference but not used:
    const directVideoSimulations_OLD: IVideoSimulation[] = [
      {
        id: 'sim-10-1-5',
        title: 'Mô phỏng Đồ thị quãng đường',
        description: 'Video mô phỏng đồ thị quãng đường - đồ thị, quãng đường',
        videoPath: '/videos/lop-10/chuong-1-dong-hoc/bai-5-do_thi_quang_duong.mp4',
        lop: 10,
        chuong: 1,
        bai: 5,
        chuongName: 'Động học chất điểm',
        baiName: 'Bài 5',
        difficulty: 'dễ',
        duration: 20,
        rating: 4.3,
        tags: ['mô phỏng', 'video', 'đồ thị', 'quãng đường'],
        type: 'mo-phong',
        url: '/lop/10/chuong/1/bai/5?section=moPhong'
      },
      {
        id: 'sim-11-1-1',
        title: 'Mô phỏng Dao động điều hòa',
        description: 'Video mô phỏng dao động điều hòa - dao động điều hòa, con lắc',
        videoPath: '/videos/lop-11/chuong-1-dao-dong/bai-1-dao-dong-dieu-hoa-phuong-trinh-dao-dong-dieu-hoa.mp4',
        lop: 11,
        chuong: 1,
        bai: 1,
        chuongName: 'Dao động cơ',
        baiName: 'Bài 1',
        difficulty: 'trung bình',
        duration: 25,
        rating: 4.5,
        tags: ['mô phỏng', 'video', 'dao động điều hòa', 'con lắc'],
        type: 'mo-phong',
        url: '/lop/11/chuong/1/bai/1?section=moPhong'
      },
      {
        id: 'sim-11-2-6',
        title: 'Mô phỏng Hiệu ứng Doppler',
        description: 'Video mô phỏng hiệu ứng doppler - sóng, giao thoa',
        videoPath: '/videos/lop-11/chuong-2-song/bai-6_Hieu_ung_Doppler.mp4',
        lop: 11,
        chuong: 2,
        bai: 6,
        chuongName: 'Sóng cơ và sóng âm',
        baiName: 'Bài 6',
        difficulty: 'khó',
        duration: 30,
        rating: 4.7,
        tags: ['mô phỏng', 'video', 'sóng', 'giao thoa'],
        type: 'mo-phong',
        url: '/lop/11/chuong/2/bai/6?section=moPhong'
      }
    ]

    // NOTE: Above array is kept for reference only, not used anymore
    // Using VideoSimulationService.getVideoSimulations() instead
  }, [])

  useEffect(() => {
    // Filter content based on selected filters - chỉ hiển thị video mô phỏng
    let filtered: ContentItem[] = []

    // Debug: Check videoSimulations state
    console.log('🎬 Filtering video simulations:', videoSimulations)
    console.log('🔍 videoSimulations type:', typeof videoSimulations)
    console.log('🔍 videoSimulations length:', videoSimulations?.length)
    console.log('🔍 Current filters - Class:', selectedClass, 'Bai:', selectedBai, 'Search:', searchTerm)

    // Early return if no video simulations
    if (!videoSimulations || videoSimulations.length === 0) {
      console.log('⚠️ No video simulations available yet')
      console.log('🔍 videoSimulations is:', videoSimulations)
      setFilteredItems([])
      return
    }

    console.log('🔍 Using videoSimulations:', videoSimulations.length, 'items')

    let filteredVideoSimulations = VideoSimulationService.filterSimulations(videoSimulations as unknown as IVideoSimulation[], {
      lop: selectedClass !== 'all' ? parseInt(selectedClass) : undefined,
      searchTerm: searchTerm || undefined
    })
    console.log('📊 After class/search filter:', filteredVideoSimulations.length, 'items')

    // Filter by bài if selected
    if (selectedBai !== 'all') {
      filteredVideoSimulations = filteredVideoSimulations.filter(sim => sim.bai === parseInt(selectedBai))
      console.log('📊 After bai filter:', filteredVideoSimulations.length, 'items')
    }

    // Convert VideoSimulation to ContentItem
    filtered = filteredVideoSimulations.map(sim => ({
      id: sim.id,
      type: sim.type as 'mo-phong',
      title: sim.title,
      description: sim.description,
      lop: sim.lop,
      chuong: sim.chuong,
      bai: sim.bai,
      difficulty: sim.difficulty,
      duration: sim.duration,
      rating: sim.rating,
      tags: sim.tags,
      url: sim.url,
      videoPath: sim.videoPath,
      chuongName: sim.chuongName,
      baiName: sim.baiName
    }))
    console.log('✅ Filtered video simulations:', filtered)
    console.log('✅ Final filtered items count:', filtered.length)

    setFilteredItems(filtered)
  }, [videoSimulations, selectedClass, selectedBai, searchTerm])

  useEffect(() => {
    // Update URL params when filters change
    const params = new URLSearchParams()
    if (selectedClass !== 'all') params.set('class', selectedClass)
    if (selectedBai !== 'all') params.set('bai', selectedBai)
    if (searchTerm) params.set('search', searchTerm)

    setSearchParams(params)
  }, [selectedClass, selectedBai, searchTerm, setSearchParams])

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'bai-hoc': return BookOpen
      case 'trac-nghiem': return Target
      case 'mo-phong': return Play
      default: return BookOpen
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'bai-hoc': return 'Bài học'
      case 'trac-nghiem': return 'Trắc nghiệm'
      case 'mo-phong': return 'Mô phỏng'
      default: return 'Nội dung'
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'bai-hoc': return 'bg-secondary-100 text-secondary-800'
      case 'trac-nghiem': return 'bg-primary-100 text-primary-800'
      case 'mo-phong': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'dễ': return 'bg-green-100 text-green-800'
      case 'trung bình': return 'bg-yellow-100 text-yellow-800'
      case 'khó': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Video giờ hiển thị inline trong card, không cần modal

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner text="Đang tải nội dung..." />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50">
      <Navigation />


      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-center mb-6">
            <div className="p-4 bg-gradient-to-r from-green-500 to-teal-600 rounded-full shadow-lg mr-6">
              <Microscope className="w-12 h-12 text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent mb-2">
                Mô phỏng Vật lí
              </h1>
              <p className="text-xl text-gray-600">
                Khám phá các mô phỏng tương tác và video minh họa
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Tìm kiếm nội dung..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              {/* Class Filter */}
              <select
                value={selectedClass}
                onChange={(e) => {
                  setSelectedClass(e.target.value)
                  setSelectedBai('all') // Reset bài khi đổi lớp
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">Tất cả lớp</option>
                <option value="10">Lớp 10</option>
                <option value="11">Lớp 11</option>
                <option value="12">Lớp 12</option>
              </select>

              {/* Bài Filter */}
              <select
                value={selectedBai}
                onChange={(e) => setSelectedBai(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={selectedClass === 'all'}
              >
                <option value="all">Tất cả bài</option>
                {getBaiListByClass(selectedClass).map(bai => (
                  <option key={bai} value={bai}>Bài {bai}</option>
                ))}
              </select>


              {/* View Mode */}
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-primary-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-primary-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 text-sm text-gray-600">
            Tìm thấy {filteredItems.length} kết quả
          </div>
        </div>

        {/* Content Grid/List */}
        {filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Không tìm thấy kết quả</h3>
            <p className="text-gray-600">Thử thay đổi bộ lọc hoặc từ khóa tìm kiếm</p>
            <div className="mt-4 text-sm text-gray-500">
              Debug: filteredItems.length = {filteredItems.length}
            </div>
          </div>
        ) : (
          <div className={viewMode === 'grid'
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
            : 'space-y-4'
          }>
            {filteredItems.map((item) => {
              const IconComponent = getTypeIcon(item.type)

              if (viewMode === 'list') {
                return (
                  <Link
                    key={item.id}
                    to={item.url}
                    className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 p-6"
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`p-3 rounded-lg ${getTypeColor(item.type)}`}>
                        <IconComponent className="w-6 h-6" />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded ${getTypeColor(item.type)}`}>
                            {getTypeLabel(item.type)}
                          </span>
                        </div>

                        <p className="text-gray-600 mb-2">{item.description}</p>

                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>Lớp {item.lop}</span>
                          {item.type !== 'mo-phong' && (
                            <>
                              <span>•</span>
                              <span className="flex items-center space-x-1">
                                <Clock className="w-4 h-4" />
                                <span>{item.duration} phút</span>
                              </span>
                            </>
                          )}
                          <span>•</span>
                          <span className={`px-2 py-1 rounded ${getDifficultyColor(item.difficulty)}`}>
                            {item.difficulty}
                          </span>
                          <span>•</span>
                          <span className="flex items-center space-x-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            <span>{item.rating}</span>
                          </span>
                        </div>
                      </div>

                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </Link>
                )
              }

              // Xử lý video mô phỏng khác với bài học
              if (item.type === 'mo-phong') {
                return (
                  <div
                    key={item.id}
                    className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
                  >
                    {/* Card Header */}
                    <div className={`p-4 ${getTypeColor(item.type)} bg-opacity-20`}>
                      <div className="flex items-center justify-between">
                        <div className={`p-2 rounded-lg ${getTypeColor(item.type)}`}>
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <span className={`px-3 py-1 text-sm font-medium rounded-full ${getTypeColor(item.type)}`}>
                          {getTypeLabel(item.type)}
                        </span>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center group-hover:text-primary-600 transition-colors">
                        {item.title.replace(/^Mô phỏng\s+/i, '')}
                      </h3>

                      {/* Video Player */}
                      <div className="mb-4">
                        <VideoSimulation
                          title={item.title}
                          description={item.description}
                          videoPath={item.videoPath}
                          showInfo={false}
                          compact={true}
                        />
                      </div>

                      {/* Metadata */}
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                        <span>Lớp {item.lop} - Chương {item.chuong} - Bài {item.bai}</span>
                      </div>

                      {/* Tags and Rating */}
                      <div className="flex items-center justify-between">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getDifficultyColor(item.difficulty)}`}>
                          {item.difficulty}
                        </span>

                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-current" />
                          <span className="text-sm text-gray-600">{item.rating}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              }

              // Xử lý bài học (redirect đến trang bài học)
              return (
                <Link
                  key={item.id}
                  to={item.url}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
                >
                  {/* Card Header */}
                  <div className={`p-4 ${getTypeColor(item.type)} bg-opacity-20`}>
                    <div className="flex items-center justify-between">
                      <div className={`p-2 rounded-lg ${getTypeColor(item.type)}`}>
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${getTypeColor(item.type)}`}>
                        {getTypeLabel(item.type)}
                      </span>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      {item.title}
                    </h3>

                    <p className="text-gray-600 mb-4 text-sm">
                      {item.description}
                    </p>

                    {/* Metadata */}
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <span>Lớp {item.lop}</span>
                      <span className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{item.duration} phút</span>
                      </span>
                    </div>

                    {/* Tags and Rating */}
                    <div className="flex items-center justify-between">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getDifficultyColor(item.difficulty)}`}>
                        {item.difficulty}
                      </span>

                      <div className="flex items-center space-x-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                        <span className="text-sm text-gray-600">{item.rating}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </motion.div>
      <Footer />

      {/* Video hiển thị inline trong card, không cần modal */}
    </div>
  )
}

export default TongHop
